/**
 * CustomerModal Component
 * Material Design 3 dialog for creating/editing customers
 * Features: React Hook Form + Zod validation, keyboard shortcuts
 */

import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
  Typography,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import type { Customer, CreateCustomerDto, UpdateCustomerDto } from '../../types/customer';
import { formatPhoneNumber } from '../../utils/formatters';
import { parsePhoneNumber } from '../../utils/customerHelpers';

// Validation schema
const customerSchema = z.object({
  name: z.string()
    .min(1, 'Name is required')
    .max(100, 'Name must be less than 100 characters'),
  email: z.string()
    .email('Invalid email format')
    .optional()
    .or(z.literal('')),
  phone: z.string()
    .regex(/^\d{10}$/, 'Phone must be 10 digits')
    .optional()
    .or(z.literal('')),
  address: z.string()
    .max(200, 'Address must be less than 200 characters')
    .optional()
    .or(z.literal('')),
  notes: z.string()
    .max(500, 'Notes must be less than 500 characters')
    .optional()
    .or(z.literal('')),
});

type FormData = z.infer<typeof customerSchema>;

interface CustomerModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateCustomerDto | UpdateCustomerDto) => Promise<void>;
  customer?: Customer | null;
  isSubmitting?: boolean;
  error?: string | null;
}

export const CustomerModal: React.FC<CustomerModalProps> = ({
  open,
  onClose,
  onSubmit,
  customer,
  isSubmitting = false,
  error = null,
}) => {
  const isEditMode = !!customer;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
    watch,
  } = useForm<FormData>({
    resolver: zodResolver(customerSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      address: '',
      notes: '',
    },
  });

  // Reset form when modal opens/closes or customer changes
  useEffect(() => {
    if (open) {
      reset({
        name: customer?.name || '',
        email: customer?.email || '',
        phone: customer?.phone ? parsePhoneNumber(customer.phone) : '',
        address: customer?.address || '',
        notes: customer?.notes || '',
      });
    }
  }, [open, customer, reset]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!open) return;

      // Escape to close
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }

      // Ctrl+Enter to submit
      if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
        event.preventDefault();
        handleSubmit(handleFormSubmit)();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose, handleSubmit]);

  const handleFormSubmit = async (data: FormData) => {
    try {
      const submitData = {
        ...data,
        email: data.email || undefined,
        phone: data.phone || undefined,
        address: data.address || undefined,
        notes: data.notes || undefined,
      };

      await onSubmit(submitData);
      onClose();
    } catch (err) {
      console.error('Failed to submit customer:', err);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  // Watch field values for character counters
  const notesValue = watch('notes') || '';
  const addressValue = watch('address') || '';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      disableRestoreFocus
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          maxHeight: isMobile ? '100vh' : '90vh',
        },
      }}
    >
      <DialogTitle
        component="div"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
          px: { xs: 2, sm: 3 },
          pt: { xs: 2, sm: 2.5 },
        }}
      >
        <Typography
          variant="h6"
          component="h2"
          sx={{
            fontWeight: 600,
            fontSize: { xs: '1.125rem', sm: '1.25rem' },
          }}
        >
          {isEditMode ? 'Edit Customer' : 'Add New Customer'}
        </Typography>
        <IconButton
          onClick={handleClose}
          disabled={isSubmitting}
          size="small"
          aria-label="close"
          sx={{
            minWidth: { xs: 44, sm: 'auto' },
            minHeight: { xs: 44, sm: 'auto' },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogContent
          sx={{
            pt: 2,
            px: { xs: 2, sm: 3 },
            pb: { xs: 2, sm: 3 },
          }}
        >
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: { xs: 2, sm: 2.5 },
            }}
          >
            {/* Name Field */}
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Customer Name"
                  required
                  fullWidth
                  autoFocus={!isMobile}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  disabled={isSubmitting}
                  inputProps={{
                    maxLength: 100,
                    autoCapitalize: 'words',
                  }}
                />
              )}
            />

            {/* Email Field */}
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Email"
                  type="email"
                  fullWidth
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  disabled={isSubmitting}
                  inputProps={{
                    inputMode: 'email',
                    autoCapitalize: 'none',
                    autoCorrect: 'off',
                  }}
                />
              )}
            />

            {/* Phone Field */}
            <Controller
              name="phone"
              control={control}
              render={({ field: { onChange, value, ...field } }) => (
                <TextField
                  {...field}
                  label="Phone Number"
                  fullWidth
                  value={value ? formatPhoneNumber(value) : ''}
                  onChange={(e) => {
                    const raw = parsePhoneNumber(e.target.value);
                    if (raw.length <= 10) {
                      onChange(raw);
                    }
                  }}
                  error={!!errors.phone}
                  helperText={errors.phone?.message || 'Format: (555) 123-4567'}
                  disabled={isSubmitting}
                  placeholder="(555) 123-4567"
                  inputProps={{
                    inputMode: 'tel',
                    type: 'tel',
                  }}
                />
              )}
            />

            {/* Address Field */}
            <Controller
              name="address"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Address"
                  fullWidth
                  multiline
                  rows={isMobile ? 2 : 2}
                  error={!!errors.address}
                  helperText={
                    errors.address?.message ||
                    `${addressValue.length}/200 characters`
                  }
                  disabled={isSubmitting}
                  inputProps={{ maxLength: 200 }}
                  sx={{
                    '& .MuiInputBase-root': {
                      fontSize: { xs: '1rem', sm: '0.875rem' },
                    },
                  }}
                />
              )}
            />

            {/* Notes Field */}
            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Notes"
                  fullWidth
                  multiline
                  rows={isMobile ? 3 : 3}
                  error={!!errors.notes}
                  helperText={
                    errors.notes?.message ||
                    `${notesValue.length}/500 characters`
                  }
                  disabled={isSubmitting}
                  inputProps={{ maxLength: 500 }}
                  sx={{
                    '& .MuiInputBase-root': {
                      fontSize: { xs: '1rem', sm: '0.875rem' },
                    },
                  }}
                />
              )}
            />
          </Box>

          {/* Keyboard Shortcuts Hint - Hidden on mobile */}
          {!isMobile && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 2, display: 'block' }}
            >
              Press Esc to cancel or Ctrl+Enter to save
            </Typography>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: 2, sm: 3 },
            pb: { xs: 2.5, sm: 2.5 },
            pt: { xs: 1.5, sm: 1 },
            gap: { xs: 1.5, sm: 1 },
            flexDirection: { xs: 'column', sm: 'row' },
          }}
        >
          <Button
            onClick={handleClose}
            disabled={isSubmitting}
            fullWidth={isMobile}
            sx={{
              textTransform: 'none',
              minHeight: { xs: 48, sm: 'auto' },
              order: { xs: 2, sm: 1 },
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting || !isDirty}
            fullWidth={isMobile}
            sx={{
              textTransform: 'none',
              minWidth: { xs: 'auto', sm: 100 },
              minHeight: { xs: 48, sm: 'auto' },
              order: { xs: 1, sm: 2 },
            }}
          >
            {isSubmitting ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CustomerModal;
